import React from 'react';
import { Phone, AlertTriangle, CheckCircle, FileText, Users, Scale } from 'lucide-react';
export function CdlDoINeedOnePage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-1920">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-1921">
        <div className="container mx-auto px-4" data-id="element-1922">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-1923">
            Commercial Driver License
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-1924">
            Complete CDL Requirements Guide
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-1925">
        <div className="max-w-4xl mx-auto" data-id="element-1926">
          {/* Who Needs a CDL */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1927">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1928">
              <FileText className="mr-3 text-[#B1050B]" size={32} data-id="element-1929" />
              Who Needs a CDL?
            </h2>

            <p className="text-lg text-gray-700 mb-6" data-id="element-1930">
              Any resident who intends to operate:
            </p>

            <div className="space-y-6" data-id="element-1931">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-1932">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-1933">VEHICLES:</h3>
                <p className="text-gray-700" data-id="element-1934">
                  • Having a gross vehicle weight rating (GVWR) of{' '}
                  <strong data-id="element-1935">26,001 pounds or more</strong>
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-1936">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-1937">
                  COMBINATION VEHICLES:
                </h3>
                <p className="text-gray-700" data-id="element-1938">
                  • Towing a trailer or other vehicles with a GVWR of{' '}
                  <strong data-id="element-1939">10,001 pounds or more</strong> with a gross
                  combination weight rating of{' '}
                  <strong data-id="element-1940">26,001 pounds or more</strong>
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-1941">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-1942">ANY VEHICLES:</h3>
                <ul className="space-y-2 text-gray-700" data-id="element-1943">
                  <li data-id="element-1944">
                    • Designed to transport <strong data-id="element-1945">16 or more people</strong>{' '}
                    (including the driver)
                  </li>
                  <li data-id="element-1946">
                    • Carrying <strong data-id="element-1947">hazardous materials</strong> in amounts
                    requiring placards
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mt-6" data-id="element-1948">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-1949">
                Important Definitions:
              </h3>
              <div className="space-y-3 text-gray-700" data-id="element-1950">
                <p data-id="element-1951">
                  <strong data-id="element-1952">Gross Vehicle Weight Rating (GVWR)</strong> is the
                  recommended maximum total weight of the vehicle and load as
                  designated by the vehicle manufacturer. The GVWR label is
                  usually found on the driver side door post of the power unit
                  and on or near the front of the trailer.
                </p>
                <p data-id="element-1953">
                  <strong data-id="element-1954">Gross Combination Weight Rating (GCWR)</strong> means
                  the value specified by the manufacturer as the maximum loaded
                  weight of a combination vehicle. In the absence of a label,
                  the GCWR will be calculated by adding the GVWR of the power
                  unit to the GVWR of the vehicle(s) or trailer(s) being towed.
                </p>
              </div>
            </div>
          </div>

          {/* Age Requirements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1955">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1956">
              How Old Must I Be To Obtain a CDL?
            </h2>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-1957">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-1958">
                <p className="text-3xl font-bold text-blue-600 mb-2" data-id="element-1959">21</p>
                <p className="font-bold text-gray-900 mb-2" data-id="element-1960">
                  Interstate Commerce
                </p>
                <p className="text-gray-700" data-id="element-1961">
                  Drive a commercial motor vehicle across state lines
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-1962">
                <p className="text-3xl font-bold text-green-600 mb-2" data-id="element-1963">18</p>
                <p className="font-bold text-gray-900 mb-2" data-id="element-1964">
                  Intrastate Commerce (Michigan only)
                </p>
                <p className="text-gray-700" data-id="element-1965">
                  Drive within Michigan (except hazardous materials requiring
                  placards - must be 21)
                </p>
              </div>
            </div>
          </div>

          {/* Driving Record Eligibility */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg shadow-md p-8 mb-8" data-id="element-1966">
            <h2 className="text-2xl font-bold text-red-900 mb-6 flex items-center" data-id="element-1967">
              <AlertTriangle className="mr-3" size={28} data-id="element-1968" />
              Driving Record Eligibility Requirements
            </h2>

            <p className="text-gray-800 mb-4" data-id="element-1969">
              Before a person may apply for an original CDL, they must qualify
              based on their driver record. Any of the following will{' '}
              <strong data-id="element-1970">disqualify</strong> a person from applying:
            </p>

            <ul className="space-y-3 text-gray-800" data-id="element-1971">
              <li className="flex items-start" data-id="element-1972">
                <span className="mr-2" data-id="element-1973">•</span>
                <span data-id="element-1974">
                  The inability to make required certifications on the CDL
                  Certification Form (BFS-103)
                </span>
              </li>
              <li className="flex items-start" data-id="element-1975">
                <span className="mr-2" data-id="element-1976">•</span>
                <span data-id="element-1977">Having a license from more than one state</span>
              </li>
              <li className="flex items-start" data-id="element-1978">
                <span className="mr-2" data-id="element-1979">•</span>
                <span data-id="element-1980">
                  An active Michigan or out-state suspension, revocation,
                  denial, or cancellation of driving privilege
                </span>
              </li>
              <li className="flex items-start" data-id="element-1981">
                <span className="mr-2" data-id="element-1982">•</span>
                <span data-id="element-1983">
                  A suspension or revocation in the 36 months immediately
                  preceding application
                </span>
              </li>
              <li className="flex items-start" data-id="element-1984">
                <span className="mr-2" data-id="element-1985">•</span>
                <span data-id="element-1986">
                  Conviction of any six-point violation in the 24 months
                  immediately preceding application
                </span>
              </li>
              <li className="flex items-start" data-id="element-1987">
                <span className="mr-2" data-id="element-1988">•</span>
                <span data-id="element-1989">
                  Conviction for operating a commercial motor vehicle while
                  impaired in the 24 months immediately preceding application
                </span>
              </li>
            </ul>

            <div className="bg-yellow-50 rounded-lg p-4 mt-4" data-id="element-1990">
              <p className="text-sm text-gray-800" data-id="element-1991">
                <strong data-id="element-1992">NOTE:</strong> Suspensions for Failure to Appear in
                Court (FAC), Failure to Comply with a Court Judgment (FCJ),
                failure to appear for a Driver Assessment re-examination,
                financial responsibility, non-sufficient funds checks, and
                suspension/revocation for temporary medical condition will NOT
                disqualify an applicant.
              </p>
            </div>
          </div>

          {/* CDL Groups */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1993">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1994">
              <Scale className="mr-3 text-[#B1050B]" size={28} data-id="element-1995" />
              What Type of CDL Do I Need?
            </h2>

            <div className="space-y-6" data-id="element-1996">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-1997">
                <h3 className="text-2xl font-bold text-blue-600 mb-3" data-id="element-1998">
                  GROUP A
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-1999">
                  Any combination of vehicles with a gross combination weight
                  rating (GCWR) of <strong data-id="element-2000">26,001 pounds or more</strong>,
                  provided the gross vehicle weight rating (GVWR) of the
                  vehicle(s) being towed is{' '}
                  <strong data-id="element-2001">10,001 pounds or more</strong>.
                </p>
                <p className="text-sm text-gray-600 italic" data-id="element-2002">
                  A Group A endorsement will allow a driver to operate a Group B
                  or Group C vehicle provided they possess required
                  endorsement(s).
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-2003">
                <h3 className="text-2xl font-bold text-green-600 mb-3" data-id="element-2004">
                  GROUP B
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-2005">
                  Any single vehicle with a gross vehicle weight rating (GVWR)
                  of <strong data-id="element-2006">26,001 pounds or more</strong>, or any such vehicle
                  towing a vehicle with a GVWR of{' '}
                  <strong data-id="element-2007">10,000 pounds or less</strong>.
                </p>
                <p className="text-sm text-gray-600 italic" data-id="element-2008">
                  A Group B endorsement will allow a driver to operate a Group C
                  vehicle provided they possess required endorsement(s).
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-2009">
                <h3 className="text-2xl font-bold text-yellow-600 mb-3" data-id="element-2010">
                  GROUP C
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-2011">
                  Any single vehicle with a GVWR of{' '}
                  <strong data-id="element-2012">26,001 pounds or less</strong>, or combination of
                  vehicles where the GVWR of the vehicle being towed is{' '}
                  <strong data-id="element-2013">10,000 pounds or less</strong> designed to:
                </p>
                <ul className="space-y-1 text-gray-700 ml-4" data-id="element-2014">
                  <li data-id="element-2015">○ Transport 16 or more persons (including the driver)</li>
                  <li data-id="element-2016">
                    ○ Transport hazardous materials in amounts requiring
                    placards
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Endorsements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2017">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2018">
              What Endorsements Do I Need?
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-2019">
              In addition to the appropriate CDL vehicle group designation,
              endorsements are required to operate the following:
            </p>

            <div className="space-y-4" data-id="element-2020">
              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-2021">
                <span className="bg-blue-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2022">
                  T
                </span>
                <div data-id="element-2023">
                  <p className="font-bold text-gray-900" data-id="element-2024">
                    DOUBLE or TRIPLE TRAILERS
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2025">
                    (Triple trailer combinations are not permitted in Michigan)
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-2026">
                <span className="bg-green-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2027">
                  P
                </span>
                <div data-id="element-2028">
                  <p className="font-bold text-gray-900" data-id="element-2029">PASSENGER</p>
                  <p className="text-sm text-gray-600" data-id="element-2030">
                    For vehicles designed to carry 16 or more people (including
                    the driver)
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-2031">
                <span className="bg-purple-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2032">
                  N
                </span>
                <div data-id="element-2033">
                  <p className="font-bold text-gray-900" data-id="element-2034">TANK</p>
                  <p className="text-sm text-gray-600" data-id="element-2035">
                    For vehicles designed to haul liquids/liquefied gases in
                    permanently mounted cargo tanks rated at 119 gallons or
                    more, or portable cargo tanks rated at 1,000 gallons or more
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-2036">
                <span className="bg-red-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2037">
                  H
                </span>
                <div data-id="element-2038">
                  <p className="font-bold text-gray-900" data-id="element-2039">HAZARDOUS MATERIALS</p>
                  <p className="text-sm text-gray-600" data-id="element-2040">
                    To carry hazardous materials in amounts requiring placards
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-2041">
                <span className="bg-orange-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2042">
                  X
                </span>
                <div data-id="element-2043">
                  <p className="font-bold text-gray-900" data-id="element-2044">
                    Combined TANK and HAZARDOUS MATERIAL
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2045">
                    When an applicant adds both the tank and hazardous materials
                    endorsements
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-start" data-id="element-2046">
                <span className="bg-yellow-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2047">
                  S
                </span>
                <div data-id="element-2048">
                  <p className="font-bold text-gray-900" data-id="element-2049">SCHOOL BUS</p>
                  <p className="text-sm text-gray-600" data-id="element-2050">
                    For commercial motor vehicles used to transport pre-primary,
                    primary, or secondary school students
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Medical Requirements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2051">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2052">
              Driver Qualification Requirements
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-2053">
              Unless exempt, you must comply with the federal commercial driver
              qualification requirements, including the medical/physical
              qualifications found in the Federal Motor Carrier Safety
              Regulations,{' '}
              <strong data-id="element-2054">49 CFR Part 391 — Qualifications of Drivers</strong>.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6" data-id="element-2055">
              <p className="text-gray-800 mb-3" data-id="element-2056">
                When applying for your CDL, you will sign a certification form
                (BFS-103) that states you meet these requirements before
                receiving your CDL.
              </p>
              <p className="text-gray-800" data-id="element-2057">
                Before taking any CDL driver skills tests, you must provide a
                valid <strong data-id="element-2058">Medical Examiner's Certificate</strong> (commonly
                known as a DOT card) or medical waiver to your examiner.
              </p>
            </div>

            <h3 className="font-bold text-gray-900 mb-4" data-id="element-2059">
              Additional Information on Medical/Physical Requirements:
            </h3>

            <div className="space-y-3 text-gray-700" data-id="element-2060">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2061">
                <p className="font-semibold mb-1" data-id="element-2062">Intrastate (in Michigan):</p>
                <p className="text-sm" data-id="element-2063">Michigan Center for Truck Safety</p>
                <a href="http://www.truckingsafety.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm" data-id="element-2064">
                  www.truckingsafety.org
                </a>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2065">
                <p className="font-semibold mb-1" data-id="element-2066">
                  Interstate (across state lines):
                </p>
                <p className="text-sm" data-id="element-2067">
                  Federal Motor Carrier Safety Administration
                </p>
                <p className="text-sm" data-id="element-2068">
                  315 W. Allegan St., Room 205, Lansing, Michigan 48933
                </p>
                <p className="text-sm" data-id="element-2069">Phone: 517/377-1866</p>
                <a href="http://www.fmcsa.dot.gov" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm" data-id="element-2070">
                  www.fmcsa.dot.gov
                </a>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2071">
                <p className="font-semibold mb-1" data-id="element-2072">School bus requirements:</p>
                <p className="text-sm" data-id="element-2073">Michigan Department of Education</p>
                <p className="text-sm" data-id="element-2074">Office of School Support Services</p>
                <p className="text-sm" data-id="element-2075">
                  P.O. Box 30008, Lansing, Michigan 48909
                </p>
                <p className="text-sm" data-id="element-2076">Phone: 517/373-6388</p>
              </div>
            </div>
          </div>

          {/* CDL Exemptions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2077">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2078">
              Are There CDL Exemptions?
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-2079">
              The following people do <strong data-id="element-2080">NOT</strong> need a Commercial
              Driver License (CDL):
            </p>

            <div className="space-y-4" data-id="element-2081">
              <div className="bg-green-50 border-l-4 border-green-500 p-4" data-id="element-2082">
                <p className="font-bold text-gray-900 mb-2" data-id="element-2083">
                  ACTIVE DUTY MILITARY
                </p>
                <p className="text-gray-700" data-id="element-2084">
                  Operating military vehicles with military licenses (includes
                  National Guard)
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4" data-id="element-2085">
                <p className="font-bold text-gray-900 mb-2" data-id="element-2086">
                  POLICE AND FIREFIGHTERS
                </p>
                <p className="text-gray-700" data-id="element-2087">
                  Operating authorized emergency vehicles
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4" data-id="element-2088">
                <p className="font-bold text-gray-900 mb-2" data-id="element-2089">FARMERS</p>
                <p className="text-gray-700 mb-2" data-id="element-2090">
                  Operating vehicles within a 150 mile radius of their farm
                </p>
                <p className="text-sm text-gray-600" data-id="element-2091">
                  An F-endorsement is needed by farmers operating combination
                  vehicles whose towing vehicle has a GVWR of 26,001 pounds or
                  more. A knowledge test, but no skills test, is required. The
                  F-endorsement is NOT a CDL.
                </p>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-4" data-id="element-2092">
                <p className="font-bold text-gray-900 mb-2" data-id="element-2093">INDIVIDUALS</p>
                <p className="text-gray-700" data-id="element-2094">
                  Operating motor homes or other vehicles used exclusively to
                  transport personal possessions or family members, for
                  non-business purposes
                </p>
              </div>
            </div>
          </div>

          {/* How to Obtain */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2095">
            <h2 className="text-3xl font-bold mb-6" data-id="element-2096">How Do I Obtain a CDL?</h2>

            <div className="space-y-4" data-id="element-2097">
              <div className="bg-white/10 rounded-lg p-4 flex items-start" data-id="element-2098">
                <span className="bg-white text-[#B1050B] font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2099">
                  1
                </span>
                <div data-id="element-2100">
                  <p className="font-semibold mb-2" data-id="element-2101">
                    Visit Secretary of State Branch Office
                  </p>
                  <ul className="text-sm space-y-1 text-gray-100" data-id="element-2102">
                    <li data-id="element-2103">• Show your driver license</li>
                    <li data-id="element-2104">• Show proof of Social Security number</li>
                    <li data-id="element-2105">• Fill out CDL Certification form (BFS-103)</li>
                    <li data-id="element-2106">• Meet driver eligibility requirements</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex items-start" data-id="element-2107">
                <span className="bg-white text-[#B1050B] font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2108">
                  2
                </span>
                <div data-id="element-2109">
                  <p className="font-semibold mb-2" data-id="element-2110">Pass Required Tests</p>
                  <p className="text-sm text-gray-100" data-id="element-2111">
                    Pass required knowledge and vision tests to obtain a CDL
                    Temporary Instruction Permit (TIP)
                  </p>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex items-start" data-id="element-2112">
                <span className="bg-white text-[#B1050B] font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2113">
                  3
                </span>
                <div data-id="element-2114">
                  <p className="font-semibold mb-2" data-id="element-2115">Pay Fees</p>
                  <p className="text-sm text-gray-100" data-id="element-2116">
                    Pay your CDL Group Designation and Endorsement fees
                  </p>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex items-start" data-id="element-2117">
                <span className="bg-white text-[#B1050B] font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2118">
                  4
                </span>
                <div data-id="element-2119">
                  <p className="font-semibold mb-2" data-id="element-2120">Schedule Skills Test</p>
                  <p className="text-sm text-gray-100" data-id="element-2121">
                    Schedule and take your CDL skills test through an approved
                    third party driver skills test organization
                  </p>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex items-start" data-id="element-2122">
                <span className="bg-white text-[#B1050B] font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2123">
                  5
                </span>
                <div data-id="element-2124">
                  <p className="font-semibold mb-2" data-id="element-2125">Receive Certificate</p>
                  <p className="text-sm text-gray-100" data-id="element-2126">
                    After passing, receive Michigan Department of State Driver
                    Skills Test Certificate (TPT-010). Valid for 1 year from
                    date of issuance.
                  </p>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex items-start" data-id="element-2127">
                <span className="bg-white text-[#B1050B] font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2128">
                  6
                </span>
                <div data-id="element-2129">
                  <p className="font-semibold mb-2" data-id="element-2130">Get Your CDL</p>
                  <p className="text-sm text-gray-100" data-id="element-2131">
                    Take skills test certificate to SOS branch office, pay
                    correction fee, and receive your photo CDL in the mail
                    within about 10 business days
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-2132">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2133">
              Need Help with CDL Requirements?
            </h2>
            <p className="text-xl mb-6" data-id="element-2134">
              Expert guidance on Commercial Driver License requirements and
              testing
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-2135">
              <Phone className="mr-3" size={24} data-id="element-2136" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-2137">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}